/**
 * Rettung abgeschnittener KI-Antworten.
 *
 * Der gemini-proxy meldet ein am Token-Limit abgeschnittenes Ergebnis als
 * `{ text: "<halbes JSON>", _truncated: true }`. Bisher war das ein
 * Totalverlust: `parseAiResponse` scheitert am unvollstaendigen JSON und gibt
 * den Rohstring zurueck, `extractBestArray` findet darin kein Array und
 * liefert []. Aus 280 sauber gelesenen LV-Positionen wurde die Meldung
 * "Keine Positionen erkannt. Ist das Dokument ein Leistungsverzeichnis?" —
 * ausgerechnet bei den langen Ausschreibungen, um die es geht.
 *
 * Hier wird stattdessen bis zum letzten VOLLSTAENDIGEN Objekt geschnitten. Der
 * Rest ist echte Beute; dass etwas fehlt, meldet das Truncation-Banner
 * getrennt.
 *
 * Rein und ohne Abhaengigkeiten, damit die Zeichenweise-Logik (Strings,
 * Escapes, verschachtelte Klammern) ohne DOM-Testumgebung pruefbar bleibt.
 */

/**
 * Findet den Beginn des gesuchten Arrays. Bevorzugt eines der genannten
 * Felder ("positions"), faellt sonst auf die erste offene eckige Klammer
 * zurueck (Antwort war ein blankes Array).
 */
const findArrayStart = (text: string, keys: readonly string[]): number => {
    for (const key of keys) {
        // "positions" : [   — Leerzeichen, einfache Anfuehrungszeichen und
        // abweichende Gross-/Kleinschreibung zulassen.
        const re = new RegExp(`["']${key}["']\\s*:\\s*\\[`, 'i');
        const m = re.exec(text);
        if (m) return m.index + m[0].length - 1;
    }

    // Kein bekannter Feldname. Das Modell hat das Array womoeglich unter einem
    // anderen Schluessel abgelegt ("lvPositionen") — dann ist die erste
    // eckige Klammer, auf die ein Objekt folgt, die richtige. Ohne diesen
    // Zweig war die Rettung SCHWAECHER als der Normalpfad, den sie ersetzt
    // (extractBestArray nimmt dort das erste Array unter beliebigem Schluessel).
    const objectArray = /\[\s*\{/.exec(text);
    if (objectArray) return objectArray.index;

    const bracket = text.indexOf('[');
    const brace = text.indexOf('{');
    // Ein blankes Array beginnt vor dem ersten Objekt; steht davor eine
    // geschweifte Klammer, war die Antwort ein Objekt ohne das gesuchte Feld.
    if (bracket >= 0 && (brace < 0 || bracket < brace)) return bracket;
    return -1;
};

/**
 * Schneidet die vollstaendigen Objekte aus einem (moeglicherweise
 * abgeschnittenen) JSON-Array heraus.
 *
 * @param text   Rohtext der KI-Antwort, mit oder ohne Markdown-Zaun.
 * @param keys   Feldnamen, unter denen das Array erwartet wird.
 * @returns      Die geparsten Objekte. Leer, wenn nicht einmal eines vollstaendig ist.
 */
export const salvageJsonArray = (text: unknown, keys: readonly string[] = []): unknown[] => {
    if (typeof text !== 'string') return [];
    const cleaned = text.replace(/```json/gi, '').replace(/```/g, '');
    const start = findArrayStart(cleaned, keys);
    if (start < 0) return [];

    const out: unknown[] = [];
    let depth = 0;          // Verschachtelungstiefe INNERHALB des Arrays
    let objStart = -1;      // Beginn des aktuellen Top-Level-Objekts
    let inString = false;
    let escaped = false;

    for (let i = start + 1; i < cleaned.length; i++) {
        const c = cleaned[i];

        if (inString) {
            if (escaped) escaped = false;
            else if (c === '\\') escaped = true;
            else if (c === '"') inString = false;
            continue;
        }

        if (c === '"') { inString = true; continue; }

        if (c === '{' || c === '[') {
            if (depth === 0 && c === '{') objStart = i;
            depth++;
            continue;
        }

        if (c === '}' || c === ']') {
            if (depth === 0) break; // schliessende Klammer des Arrays selbst
            depth--;
            if (depth === 0 && objStart >= 0 && c === '}') {
                const slice = cleaned.slice(objStart, i + 1);
                try {
                    out.push(JSON.parse(slice));
                } catch {
                    // Ein einzelnes unparsbares Objekt darf den Rest nicht
                    // mitreissen — ueberspringen und weiterlaufen.
                }
                objStart = -1;
            }
        }
    }

    return out;
};

/**
 * Liest die Abschneide-Marke des Proxys aus der ROHEN Antwort.
 *
 * Muss vor `parseAiResponse` laufen: das entpackt `.text` als erstes und wirft
 * dabei alle Geschwisterfelder weg — samt `_truncated`. Die Marke ging dadurch
 * genau in dem Fall verloren, fuer den sie gebaut wurde (unparsbares JSON).
 */
export const isTruncatedAiResponse = (raw: unknown): boolean => {
    if (!raw || typeof raw !== 'object') return false;
    return (raw as Record<string, unknown>)._truncated === true;
};

/** Der Rohtext einer Antwort, die der Proxy nicht als JSON parsen konnte. */
export const rawAiText = (raw: unknown): string => {
    if (typeof raw === 'string') return raw;
    if (raw && typeof raw === 'object') {
        const t = (raw as Record<string, unknown>).text;
        if (typeof t === 'string') return t;
    }
    return '';
};
